import {_} from 'lodash';
import * as ko from 'knockout';
import {IMessageData, IObjectData, IRouteDefinitionParams} from '../../index';
import {global} from '../../appContext';
import {HIERARCHY_ARROW, REQUEST_THROTTLE} from '../../constants';
import {REQUEST_MESSAGE} from '../../constants/Language';
import {createErrorMessage, getKnockoutThrottle, getRawSyncFieldValue} from '../../util/misc';
import {nsRoute} from '../../util/route';
import hierarchyHtml from './hierarchy.html?raw';
import ParsedRouteDataDecorator = nsRoute.ParsedRouteDataDecorator;

export let template = hierarchyHtml;

interface ILegalUnitNode {
    above: ILegalUnitNode | null
    below: ILegalUnitNode[]
    data: IObjectData
    id: number
    isLast: boolean
    name: string
    organizationNumber: string
    parentOrganizationNumber: string
}

export class viewModel extends ParsedRouteDataDecorator {
    private readonly HIERARCHY_ARROW = HIERARCHY_ARROW;
    private readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    private readonly isReady: KnockoutObservable<boolean> = ko.observable(false);
    private readonly searchKey = ko.observable('').extend(getKnockoutThrottle({rateLimit: REQUEST_THROTTLE}));
    private readonly isFiltered = ko.pureComputed(() => !!this.searchKey());
    private readonly searchBadgeWaiting = ko.pureComputed(() => !this.isReady());
    private readonly searchBadge = ko.pureComputed(() => {
        if (!this.isReady()) {
            return null;
        }
        return this.isFiltered() ? this.filtered().length : this.units.length;
    });

    private units: ILegalUnitNode[] = [];
    private allTop: ILegalUnitNode[] = [];
    private current: ILegalUnitNode | null = null;
    private path: string[] = [];

    constructor(params: IRouteDefinitionParams) {
        super(params);
        this.messageData({message: REQUEST_MESSAGE});
        this.__requestRouteData?.request()
            .then((data: IObjectData[]) => {
                this.initUnits(data);
                this.messageData(data.length ? null : {message: 'no data', type: 'warning'});
                this.isReady(true);
                window.requestAnimationFrame(() => {
                    document.getElementById(`legalunit${this.__id ?? ''}`)
                        ?.scrollIntoView({behavior: 'smooth', block: 'nearest'});
                });
            })
            .catch((reason: string) => {
                this.messageData(createErrorMessage(String(reason)));
            });
    }

    private initUnits(data: IObjectData[]): void {
        const routeId = this.__id ?? global.getRouteId();
        const units = data.map((obj): ILegalUnitNode => {
            const syncfields = obj.syncfields ?? [];
            // Use raw values so missing fields stay falsy (getSyncFieldValue returns "—").
            const organizationNumber =
                getRawSyncFieldValue('ExtensionAttribute1', syncfields)
                || getRawSyncFieldValue('SourceId', syncfields)
                || '';
            const node: ILegalUnitNode = {
                above: null,
                below: [],
                data: obj,
                id: obj.id,
                isLast: false,
                name: getRawSyncFieldValue('Name', syncfields) || organizationNumber || '—',
                organizationNumber,
                parentOrganizationNumber: getRawSyncFieldValue('ExtensionAttribute3', syncfields) || '',
            };
            if (obj.id == routeId) {
                this.current = node;
            }
            return node;
        });

        const byOrganizationNumber = new Map(units.map(unit => [unit.organizationNumber, unit]));
        units.forEach(unit => {
            if (!unit.parentOrganizationNumber || unit.parentOrganizationNumber == unit.organizationNumber) {
                return;
            }
            const parent = byOrganizationNumber.get(unit.parentOrganizationNumber);
            if (parent && !this.isBelow(unit.below, parent)) {
                unit.above = parent;
                parent.below.push(unit);
            }
        });

        units.forEach(unit => unit.below = this.sort(unit.below));
        this.units = this.sort(units);
        this.allTop = this.units.filter(unit => !unit.above);
        this.allTop.forEach(unit => unit.isLast = false);
        if (this.allTop.length) {
            this.allTop[this.allTop.length - 1]!.isLast = true;
        }
        this.units.forEach(unit => {
            if (unit.above?.below.length) {
                unit.isLast = unit.above.below[unit.above.below.length - 1] == unit;
            }
        });
        this.path = this.current ? this.getPath(this.current) : [];
    }

    private sort(units: ILegalUnitNode[]): ILegalUnitNode[] {
        return _.sortBy(units, (unit: ILegalUnitNode) => unit.name.toLocaleLowerCase());
    }

    private isBelow(below: ILegalUnitNode[], needle: ILegalUnitNode): boolean {
        return below.some(unit => unit == needle || this.isBelow(unit.below, needle));
    }

    private getPath(unit: ILegalUnitNode, path: string[] = []): string[] {
        path.unshift(unit.organizationNumber);
        return unit.above ? this.getPath(unit.above, path) : path;
    }

    private getUnitsToTop(unit: ILegalUnitNode, path: ILegalUnitNode[] = []): ILegalUnitNode[] {
        path.unshift(unit);
        return unit.above ? this.getUnitsToTop(unit.above, path) : path;
    }

    private isPath(unit: ILegalUnitNode): boolean {
        return this.path.includes(unit.organizationNumber);
    }

    private getRowCls(unit: ILegalUnitNode): string | undefined {
        return this.isPath(unit) ? 'node-active' : undefined;
    }

    private getNameCls(unit: ILegalUnitNode): string | undefined {
        return unit == this.current ? 'node-selected' : undefined;
    }

    private filtered = ko.pureComputed(() => {
        const searchKey = this.searchKey().toLocaleLowerCase();
        return _.sortBy(
            this.units
                .filter(unit =>
                    unit.name.toLocaleLowerCase().includes(searchKey)
                    || unit.organizationNumber.includes(searchKey))
                .map(unit => this.getUnitsToTop(unit)),
            (path: ILegalUnitNode[]) => path.map((unit: ILegalUnitNode) => unit.name.toLocaleLowerCase()).join(''),
        );
    });

    private isLeaf(unit: ILegalUnitNode, path: ILegalUnitNode[]): boolean {
        return path[path.length - 1] == unit;
    }
}
